/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, FormEvent } from "react";
import {
  Button,
  Segment,
  Portal,
  Header,
  Table,
  Form,
  TextArea,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import {
  getApplications,
  setApplicationConfig,
} from "./apsApi";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";

interface ApplicationsProps {
  org: any;
  onCloseDialog: any;
}

const Applications: React.FC<ApplicationsProps> = ({ org, onCloseDialog }) => {
  const [apps, setApps] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [showAppConfig, setShowAppConfig] = useState<any>(null);
  const [config, setConfig] = useState("");
  const [configValid, setConfigValid] = useState(false);

  const setAppConfig = async (app: any, config: any) => {
    setLoading(true);
    const title = "Set app configuration";
    try {
      await setApplicationConfig(app.id, config ? JSON.parse(config) : null);
      setMessage({
        title: title,
        message: "App configuration was set successfully.",
      });
    } catch (e) {
      const errorMessage =
        (e.response && e.response.data && e.response.data.errorMessage) || null;
      let message = `Failed to set app config: ${errorMessage}`;
      if (errorMessage === "INVALID_INPUT") {
        message =
          "Configuration failed, specified app configuration format is invalid.";
      } else if (errorMessage === "ERROR_UNSUPPORTED_OS") {
        message =
          "Configuration can not be set to this app, OS is not supported.";
      }
      setMessage({
        title: title,
        message: message,
      });
    }
    getApplicationsForOrg();
  };

  const getApplicationsForOrg = async () => {
    setLoading(true);
    const appsList = await getApplications(org.id, 'ALL');
    setApps(appsList);
    setLoading(false);
    setConfigValid(false);
  };

  const onMount = () => {
    getApplicationsForOrg();
  };

  const appConfigCell = (app: any) => {
    if (app.protection_configuration) {
      return (
        <div
          className={styles.appConfigLink}
          onClick={() => {
            setShowAppConfig(app);
            setConfig(JSON.stringify(app.protection_configuration, null, 2));
          }}
        >
          Edit
        </div>
      );
    } else {
      return (
        <div
          className={styles.appConfigLink}
          onClick={() => setShowAppConfig(app)}
        >
          Not set
        </div>
      );
    }
  };

  const subscriptionType = (app: any):string => {
    switch(app.subscriptionType){
      case "XTD_PLATFORM":
        return "XTD"
      case "COUNTERSPY_PLATFORM":
        return "Counterspy"
      case "APPSHIELD_PLATFORM":
      case "APPSHIELD_STANDALONE":
        return "App Shield"
      default:
        return app.subscriptionType ? app.subscriptionType : "Unknown"
    }
  }

  const appRow = (app: any) => {
    return (
      <Table.Row key={app.id}>
        <Table.Cell>
          {(app.iconUrl && (
            <img
              src={app.iconUrl}
              alt="App icon"
              style={{
                width: "24px",
                height: "24px",
                margin: "0 5px 0 0",
                maxWidth: "none",
                verticalAlign: "middle",
              }}
            />
          )) || (
            <Ico
              name={app.os === "android" ? "android" : "ios"}
              style={{
                margin: "0 5px 0 0",
                width: "24px",
                filter: "contrast(0.5)",
                maxWidth: "none",
              }}
            />
          )}
          {app.applicationName}
        </Table.Cell>
        <Table.Cell>{app.applicationPackageId}</Table.Cell>
        <Table.Cell>{app.os === "android" ? "Android" : "iOS"}</Table.Cell>
        <Table.Cell>{subscriptionType(app)}</Table.Cell>
        <Table.Cell>{appConfigCell(app)}</Table.Cell>
      </Table.Row>
    );
  };

  const appConfigDialog = () => {
    return (
      <Portal open={!!showAppConfig} onClose={() => setShowAppConfig(null)}>
        <Segment id={styles.appConfigSegment}>
          <Header>{`Configuration for ${showAppConfig.applicationName}`}</Header>

          <Form>
            <TextArea
              spellCheck="false"
              placeholder="App configuration JSON"
              id={styles.appConfig}
              onChange={(event: FormEvent<HTMLTextAreaElement>) => {
                const { value } = event.currentTarget;
                try {
                  JSON.parse(value);
                  setConfigValid(true);
                } catch {
                  setConfigValid(false);
                }
                setConfig(value);
              }}
              value={config}
              className={cStyles.input}
            />
          </Form>

          <div style={{ marginTop: "2rem", textAlign: "right" }}>
            <Button
              style={{ float: "left" }}
              secondary
              className={cStyles.buttonSecond}
              onClick={() => {
                setAppConfig(showAppConfig, "");
                setShowAppConfig(null);
                setConfig("");
              }}
            >
              <Ico name="trash" style={{ marginRight: "10px" }} />
              Delete
            </Button>

            <Button
              style={{ marginRight: "2rem" }}
              secondary
              className={cStyles.buttonSecond}
              onClick={() => {
                setShowAppConfig(null);
                setConfig("");
              }}
            >
              <Ico name="cancel" style={{ marginRight: "10px" }} />
              Cancel
            </Button>

            <Button
              primary
              className={cStyles.buttonPrime}
              disabled={!configValid}
              onClick={() => {
                setAppConfig(showAppConfig, config);
                setShowAppConfig(null);
                setConfig("");
              }}
            >
              <Ico name="check" style={{ marginRight: "10px" }} />
              Save
            </Button>
          </div>
        </Segment>
      </Portal>
    );
  };

  return (
    <Portal
      open={true}
      onMount={onMount}
      closeOnEscape
      closeOnDocumentClick
      onClose={() => {
        !showAppConfig && !message && onCloseDialog(null);
      }}
    >
      <Segment loading={loading} id={styles.appsSegment}>
        <Header>Applications for {org.name}</Header>
        {apps && (
          <div style={{ overflowX: "auto" }}>
            <Table celled className={styles.usersTable}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Package ID</Table.HeaderCell>
                  <Table.HeaderCell>OS</Table.HeaderCell>
                  <Table.HeaderCell>Subscription</Table.HeaderCell>
                  <Table.HeaderCell>Configuration</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{apps.map((app: any) => appRow(app))}</Table.Body>
            </Table>
          </div>
        )}

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => setMessage(null)}
          />
        )}

        <div style={{ textAlign: "right" }}>
          <Button
            secondary
            className={cStyles.buttonSecond}
            onClick={() => onCloseDialog(null)}
          >
            <Ico name="cancel" style={{ marginRight: "10px" }} />
            Close
          </Button>
        </div>

        {showAppConfig && appConfigDialog()}
      </Segment>
    </Portal>
  );
};

export default Applications;
